<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-start mb-1">
      <div>
        <h4 class="font-weight-bold">Product Profitability</h4>
        <div class="text-muted">
          Betting profitability by product type (single, multi, exotic, etc.)
        </div>
      </div>

      <div>
        <b-button
          variant="primary"
          @click="handleExport"
          :disabled="loadingProductProfitability"
        >
          <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
          Export
        </b-button>
      </div>
    </div>

    <!-- SUMMARY STATS -->
    <b-card>
      <div>
        <b-row>
          <b-col cols="12" sm="6" md="4">
            <div class="stat-box">
              <div class="text-muted small">Total Turnover</div>
              <div class="h5 font-weight-bold">
                ${{ totalTurnover.toFixed(2) }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6" md="4">
            <div class="stat-box">
              <div class="text-muted small">Total GGR</div>
              <div class="h5 font-weight-bold text-success">
                ${{ totalGgr.toFixed(2) }}
              </div>
            </div>
          </b-col>
          <b-col cols="12" sm="6" md="4">
            <div class="stat-box">
              <div class="text-muted small">Overall Margin %</div>
              <div class="h5 font-weight-bold">
                {{ overallMarginPct.toFixed(2) }}%
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- TABLE -->
    <b-card no-body>
      <div class="m-2">
        <label
          >Profitability by Product ({{ mappedData.length }} products)</label
        >
      </div>

      <b-table
        :items="mappedData"
        :fields="tableFields"
        show-empty
        responsive
        :busy="loadingProductProfitability"
      >
        <template #cell(betType)="{ item }">
          <b-badge variant="primary">{{ item.betType }}</b-badge>
        </template>

        <template #cell(turnover)="{ item }">
          <span class="font-weight-semibold">{{
            formatCurrencyBuiltInFunc(item.turnover)
          }}</span>
        </template>

        <template #cell(ggr)="{ item }">
          <span class="font-weight-semibold text-success">{{
            formatCurrencyBuiltInFunc(item.ggr)
          }}</span>
        </template>

        <template #cell(ggrTurnoverPct)="{ item }">
          <b-badge :variant="item.ggrTurnoverPct > 0 ? 'success' : 'danger'">
            {{ item.ggrTurnoverPct.toFixed(2) }}%
          </b-badge>
        </template>

        <template #cell(ggrPctOfTotalGgr)="{ item }">
          <div class="d-flex align-items-center" style="gap: 8px">
            <div style="flex: 1">
              <b-progress
                :value="item.ggrPctOfTotalGgr"
                :variant="getVariant(item.ggrPctOfTotalGgr)"
              />
            </div>
            <span class="small">{{ item.ggrPctOfTotalGgr.toFixed(2) }}%</span>
          </div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BProgress,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BProgress,
  },
  data() {
    return {
      tableFields: [
        { key: "betType", label: "Product Type" },
        { key: "turnover", label: "Turnover" },
        { key: "ggr", label: "GGR" },
        { key: "ggrTurnoverPct", label: "Margin %" },
        { key: "ggrPctOfTotalGgr", label: "% of Total GGR" },
      ],
    };
  },
  computed: {
    ...mapState("reporting", [
      "productProfitability",
      "loadingProductProfitability",
    ]),
    mappedData() {
      return (this.productProfitability || []).map((item) => ({
        ...item,
        turnover: parseFloat(item.turnover),
        ggr: parseFloat(item.ggr),
        ggrTurnoverPct: parseFloat(item.ggrTurnoverPct),
        ggrPctOfTotalGgr: parseFloat(item.ggrPctOfTotalGgr),
      }));
    },
    totalTurnover() {
      return this.mappedData.reduce((sum, item) => sum + item.turnover, 0);
    },
    totalGgr() {
      return this.mappedData.reduce((sum, item) => sum + item.ggr, 0);
    },
    overallMarginPct() {
      const total = this.totalTurnover;
      if (total === 0) return 0;
      return (this.totalGgr / total) * 100;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    ...mapActions("reporting", ["getProductProfitability"]),
    formatCurrencyBuiltInFunc,
    async fetchData() {
      await this.getProductProfitability();
    },
    getVariant(value) {
      if (value >= 50) return "success";
      if (value >= 25) return "info";
      if (value >= 10) return "warning";
      return "danger";
    },
    handleExport() {
      alert("Export functionality to be implemented");
    },
  },
};
</script>

<style scoped>
.stat-box {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
}
</style>
